import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw, Download, Trash2, Mail, Inbox } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";

const LeadMagnetSignups = ({ adminPassword }) => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchSignups = useCallback(async () => {
    if (!adminPassword) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/lead-magnet-signups`, {
        params: { password: adminPassword },
      });
      setSignups(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load signups";
      toast.error(typeof msg === "string" ? msg : "Failed to load signups");
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    fetchSignups();
  }, [fetchSignups]);

  const formatDate = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return ts;
    }
  };

  const handleDelete = async (signup) => {
    if (!window.confirm(`Remove ${signup.email} from your list?`)) return;
    setDeletingId(signup.id);
    try {
      await axios.delete(`${API}/admin/lead-magnet-signups/${signup.id}`, {
        params: { password: adminPassword },
      });
      setSignups((prev) => prev.filter((s) => s.id !== signup.id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const exportCsv = () => {
    if (signups.length === 0) {
      toast.error("No signups to export");
      return;
    }
    const header = ["First Name", "Email", "Source", "Page", "Date (UTC)"];
    const rows = signups.map((s) => [
      s.first_name || "",
      s.email,
      s.source || "",
      s.page_path || "",
      s.timestamp,
    ]);
    const escape = (val) => {
      const str = String(val ?? "");
      if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };
    const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `email-list-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${signups.length} signup${signups.length === 1 ? "" : "s"}`);
  };

  // Group counts by source for the summary chips
  const sourceCounts = signups.reduce((acc, s) => {
    const key = s.source || "unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="lead-magnet-signups"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h2 className="font-serif text-xl font-semibold text-foreground">
            Email List
          </h2>
          <p className="text-sm text-muted-foreground">
            {signups.length} total signup{signups.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSignups}
            disabled={loading}
            className="rounded-full"
            data-testid="signups-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={exportCsv}
            disabled={signups.length === 0}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="signups-export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Source breakdown chips */}
      {signups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="signups-source-chips">
          {Object.entries(sourceCounts).map(([src, count]) => (
            <Badge
              key={src}
              className="bg-primary/10 text-primary border-0 px-3 py-1 rounded-full"
            >
              {src.replace(/-/g, " ")} · {count}
            </Badge>
          ))}
        </div>
      )}

      {/* Table */}
      {signups.length === 0 ? (
        <Card className="bg-white rounded-2xl p-12 text-center" data-testid="signups-empty">
          <Inbox className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">
            No signups yet. They will appear here as soon as someone requests
            the free chapter.
          </p>
        </Card>
      ) : (
        <Card className="bg-white rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>First Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="hidden md:table-cell">Source</TableHead>
                <TableHead className="hidden lg:table-cell">Page</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signups.map((s) => (
                <TableRow key={s.id} data-testid={`signup-row-${s.id}`}>
                  <TableCell className="font-medium">
                    {s.first_name || (
                      <span className="text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <a
                      href={`mailto:${s.email}`}
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      {s.email}
                    </a>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="outline" className="rounded-full text-xs">
                      {s.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground truncate max-w-[200px]">
                    {s.page_path || "—"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(s.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(s)}
                      disabled={deletingId === s.id}
                      className="h-8 w-8 text-muted-foreground hover:text-red-600"
                      data-testid={`signup-delete-${s.id}`}
                      title="Remove from list"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </motion.div>
  );
};

export default LeadMagnetSignups;
